import { z } from 'zod';

import type { AssociatedType } from '@/models/AssociatedType.ts';
import type { Authority } from '@/models/Authority.ts';
import type { Connection } from '@/models/Connection.ts';
import type { Device } from '@/models/Device.ts';
import type { PersonInfo } from '@/models/PersonInfo.ts';
import type { Scheme } from '@/models/Scheme.ts';
import type { ServiceUser } from '@/models/ServiceUser.ts';

import type { Association } from './Association';

export const CALL_TYPES = [
  'INBOUND',
  'OUTBOUND',
  'UNKNOWN',
  'NOTIFICATION',
] as const;
export const CallTypeSchema = z.enum(CALL_TYPES);
export type CallType = z.infer<typeof CallTypeSchema>;

export const EVENT_TYPES = ['NORMAL', 'FIRE_ALARM', 'UNKNOWN'] as const;
export const EventTypeSchema = z.enum(EVENT_TYPES);
export type EventType = z.infer<typeof EventTypeSchema>;

export const SOURCE_TYPES = [
  'DEVICE',
  'PHONE',
  'NOTIFICATION',
  'UNKNOWN',
] as const;
export const SourceTypeSchema = z.enum(SOURCE_TYPES);
export type SourceType = z.infer<typeof SourceTypeSchema>;

export const CallStatus = {
  WAITING: 'WAITING',
  PENDING: 'PENDING',
  MISSED: 'MISSED',
  IN_PROGRESS: 'IN_PROGRESS',
  ENDED: 'ENDED',
  REJECTED: 'REJECTED',
  COMPLETED: 'COMPLETED',
} as const;

export type CallStatus = (typeof CallStatus)[keyof typeof CallStatus];

export const CallStatusSchema = z.enum([
  'WAITING',
  'PENDING',
  'MISSED',
  'IN_PROGRESS',
  'ENDED',
  'REJECTED',
  'COMPLETED',
]);

export const CallInfoSchema = z.object({
  call: z.custom<Call>(),
  person: z.custom<PersonInfo>().optional(),
  connection: z.custom<Connection>().optional(),
  device: z.custom<Device>().optional(),
  alarm: z.custom<CallAlarm>().optional(),
  type: z.enum(['SERVICE_USER', 'SCHEME', 'AUTHORITY', 'DEVICE']).optional(),
  serviceUsers: z.array(z.custom<ServiceUser>()).optional(),
  schemes: z.array(z.custom<Scheme>()).optional(),
  authorities: z.array(z.custom<Authority>()).optional(),
  associations: z.array(z.custom<Association>()).optional(),
});
export type CallInfo = z.infer<typeof CallInfoSchema>;

export const CallSchema = z.object({
  id: z.string(),
  callSessionId: z.string(),
  outsideNumber: z.string(),
  platformNumber: z.string().nullable().optional(),
  receiveTime: z.string().nullable().optional(),
  deliverTime: z.string().nullable().optional(),
  answerTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
  createDate: z.string(),
  lastUpdateDate: z.string().nullable().optional(),
  callType: CallTypeSchema,
  eventType: EventTypeSchema,
  sourceType: SourceTypeSchema,
  tempNotes: z.boolean().nullable().optional(),
  isNotificationCall: z.boolean().nullable().optional(),
  personId: z.string().nullable().optional(),
  personName: z.string().nullable().optional(),
  personPhoneNumber: z.string().nullable().optional(),
  personAddress: z.string().nullable().optional(),
  personType: z.enum(['SERVICE_USER', 'CONTACT']).nullable().optional(),
  connectionId: z.string().nullable().optional(),
  connectionType: z.string().nullable().optional(),
  connectionName: z.string().nullable().optional(),
  connectionPhoneNumber: z.string().nullable().optional(),
  connectAddress: z.string().nullable().optional(),
  deviceId: z.string().nullable().optional(),
  deviceType: z.string().nullable().optional(),
  deviceName: z.string().nullable().optional(),
  devicePhoneNumber: z.string().nullable().optional(),
  deviceAddress: z.string().nullable().optional(),
  operatorId: z.string().nullable().optional(),
  operatorName: z.string().nullable().optional(),
  operatorPhoneNumber: z.string().nullable().optional(),
  reasonOptionId: z.string().nullable().optional(),
  outcomeOptionId: z.string().nullable().optional(),
  associatedId: z.string().nullable().optional(),
  associatedType: z.custom<AssociatedType>().nullable().optional(),
  associatedName: z.string().nullable().optional(),
  associatedRelationship: z.string().nullable().optional(),
  note: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .nullable()
    .optional(),
  audioPath: z.string().nullable().optional(),
  audioName: z.string().nullable().optional(),
  audioDuration: z.number().nullable().optional(),
  alarm: z.custom<CallAlarm>().nullable().optional(),
  presetAssociations: z.array(z.custom<Association>()).nullable().optional(),
  local: z.boolean().nullable().optional(),
  status: CallStatusSchema,
});
export type Call = z.infer<typeof CallSchema>;

export const CallAlarmSchema = z.object({
  sessionId: z.string(),
  authorityId: z.string(),
  schemeId: z.string(),
  deviceId: z.string(),
  ivrServer: z.string(),
  customerSiteId: z.string().nullable(),
  domain: z.string(),
  sourceAlarmProtocol: z.string(),
  targetAlarmProtocol: z.string(),
  sipFrom: z.string(),
  calledParty: z.string(),
  eventType: z.string(),
  deviceType: z.string(),
  deviceComponent: z.string(),
  statusCode: z.string(),
  location: z.string(),
  priority: z.number(),
  controllerId: z.string(),
  audio: z.string(),
  systemConfig: z.any().nullable(),
  replyId: z.string(),
  action: z.string(),
  traceId: z.string(),
  id: z.string(),
  createDate: z.string(),
  lastUpdateDate: z.string(),
});
export type CallAlarm = z.infer<typeof CallAlarmSchema>;
